import translate from '@allesauto/i18n';
import { pushAnalyticsEvent } from '@allesauto/utils/analytics';
import { NavigationIconLinkType } from '@allesauto/utils/consts';
import { Close20 } from '@carbon/icons-react';
import clsx from 'clsx';
import { useRouter } from 'next/router';
import React, { FC, useEffect } from 'react';
import { useIntl } from 'react-intl';

import { LinkItem } from '.';
import { type INavigationMenu } from './HeaderMenu';
import Accordion from '../accordion';
import NavigationIconLink from '../navigationIconLink';
import NextLink from '../nextLink';

interface HeaderMenuMobileProps {
  open: boolean;
  handleClose: () => void;
  navigation?: INavigationMenu[];
  footerNavigation?: {
    generalLinks: LinkItem[];
    accountLinks: LinkItem[];
  };
  isLogged: boolean;
  hasMessageNotification: boolean;
  onLogout?: () => void;
  isWhiteLabel?: boolean;
}

const HeaderMenuMobile: FC<HeaderMenuMobileProps> = ({
  open,
  handleClose,
  navigation,
  footerNavigation,
  isLogged,
  hasMessageNotification,
  onLogout,
  isWhiteLabel = false,
}) => {
  const { generalLinks, accountLinks } = footerNavigation || {};

  const accordionContext = 'header';
  const router = useRouter();
  const intl = useIntl();
  const myAccountTitle = translate(intl, 'ui.HEADER.USER_CART.MY_ACCOUNT');
  const currentPath = router.asPath;

  const pushToDataLayer = (category: string, itemName: string) => {
    // dataLayer push after click on navigation
    pushAnalyticsEvent({
      event: 'navigation',
      // category: main (top navigation), sidebar (account's sidebar)
      navigation_category: category,
      navigation_item: itemName.toLowerCase(),
    });
  };

  //Close overlay on eveny key escape or unmount if is opened
  useEffect(() => {
    if (!open || typeof window === 'undefined') return;

    const keyDownEscapeHandler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        handleClose();
      }
    };
    document.addEventListener('keydown', (e) => keyDownEscapeHandler(e));
    document.body.classList.add('overflow-hidden');
    return () => {
      handleClose();
      document.removeEventListener('keydown', (e) => keyDownEscapeHandler(e));
      document.body.classList.remove('overflow-hidden');
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const className = {
    accordionContainerClass: 'col-end-23 col-start-3',
    wrapperClass: clsx(
      '-translate-x-full duration-300 ease-in-out flex h-full inset-0 transform transition z-[51] sm:fixed lg:hidden',
      open && 'translate-x-0'
    ),
    accountLinksContainerClass: 'col-end-23 col-start-1 text-white',
    closeButtonContainerClass:
      'col-end-23 col-start-3 flex h-[70px] justify-end',
    closeButtonClass:
      'inline-flex items-center justify-center text-white outline-none',
    containerClass:
      'auto-rows-min bg-grey90 grid grid-cols-24 overflow-y-auto pb-12 relative w-full',
    generalLinksContainerClass: 'col-end-23 col-start-3 mt-60px text-white',
    linkClass:
      'duration-300 font-regular no-underline text-p1 text-white transition-colors hover:text-grey50 overflow-hidden text-ellipsis whitespace-nowrap block monochrome:text-white monochrome:hover:text-grey50',
    linkClassActive: 'text-green60',
    sectionGridClass: 'col-end-25 col-start-3',
    sectionTitleClass:
      'font-regular font-weight-bold pb-[16px] text-green60 text-p1 overflow-hidden text-ellipsis whitespace-nowrap block wl-text-brand',
  };

  const formatAccordionContent = (
    sections: {
      id: string;
      name: string;
      items: {
        name: string;
        href: string;
      }[];
    }[]
  ) => {
    return (
      <div className={className.sectionGridClass}>
        {sections.map((section, index: number) => (
          <div
            className={clsx(
              index === sections.length - 1 ? 'mb-10px' : 'mb-30px'
            )}
            key={section.id + index}
          >
            <p className={className.sectionTitleClass}>{section.name}</p>
            <ul>
              {section.items.map((item, index: number) => (
                <li
                  key={item.name + index}
                  className={clsx(
                    index !== section.items.length - 1 ? 'pb-20px' : 'pb-0',
                    'relative'
                  )}
                >
                  {/* TODO: Remove this workaround after filters are fixed */}
                  {section.name === 'Unsere Fahrzeuge' ? (
                    <a
                      href={item.href}
                      className={clsx(
                        className.linkClass,
                        currentPath === item.href && className.linkClassActive
                      )}
                      onClick={() => pushToDataLayer('main', item.name)}
                    >
                      {item.name}
                    </a>
                  ) : (
                    <NextLink
                      href={item.href}
                      className={clsx(
                        className.linkClass,
                        currentPath === item.href && className.linkClassActive
                      )}
                      onClick={() => pushToDataLayer('main', item.name)}
                    >
                      {item.name}
                    </NextLink>
                  )}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    );
  };

  const navigationItems = navigation
    ? navigation.map((navItem) => {
        return {
          _uid: navItem.id,
          title: navItem.name,
          children: formatAccordionContent(navItem.sections),
          context: accordionContext as typeof accordionContext,
        };
      })
    : [];

  const accountItems =
    accountLinks &&
    accountLinks.filter((account) =>
      isLogged
        ? account.id !== 'register' && account.id !== 'login'
        : account.id === 'register' || account.id === 'login'
    );

  const accountInfoItem = {
    _uid: myAccountTitle,
    title: myAccountTitle,
    children: (
      <div className={className.accountLinksContainerClass}>
        {accountItems &&
          accountItems.map((accountItem, index: number) => (
            <NavigationIconLink
              icon={accountItem.iconType as NavigationIconLinkType}
              name={accountItem.name}
              href={accountItem.href}
              key={accountItem.id}
              hasNotification={
                hasMessageNotification && accountItem.id === 'myaccount'
              }
              isLastAccount={index === accountItems.length - 1}
              onClickHandler={
                accountItem.id === 'logout'
                  ? onLogout
                  : () => pushToDataLayer('sidebar', accountItem.name)
              }
              isActive={
                currentPath.includes(accountItem.href) &&
                accountItem.id !== 'logout'
              }
            />
          ))}
      </div>
    ),
    context: accordionContext as typeof accordionContext,
  };

  return (
    <div className={className.wrapperClass}>
      <div className={className.containerClass}>
        <div className={className.closeButtonContainerClass}>
          <button
            type="button"
            className={className.closeButtonClass}
            onClick={handleClose}
          >
            <Close20 aria-hidden="true" />
          </button>
        </div>
        <div className={className.accordionContainerClass}>
          <Accordion
            isMultiple={false}
            items={[...navigationItems, accountInfoItem]}
            component={''}
            _uid={''}
          />
        </div>
        <div className={className.generalLinksContainerClass}>
          {!isWhiteLabel &&
            generalLinks &&
            generalLinks.map((generalLink, index: number) => (
              <NavigationIconLink
                icon={generalLink.iconType as NavigationIconLinkType}
                name={generalLink.name}
                href={generalLink.href}
                key={generalLink.id + index}
                isLastGeneral={index === generalLinks.length - 1}
              />
            ))}
        </div>
      </div>
    </div>
  );
};

export default HeaderMenuMobile;
